import economies from "@/config/economies";
import regions from "@/config/regions";
import sectors from "@/config/sectors";
import subSectors from "@/config/sub-sectors";

export const formatMoney = (
  rawValue: string | number,
  skipLessThan?: boolean
) => {
  const value = typeof rawValue === "number" ? rawValue : parseInt(rawValue);
  const absValue = Math.abs(value);
  const prefix = value < 0 ? "-" : "";
  let formattedValue = "";

  console.log("value", value);

  if (absValue === 0 && !skipLessThan) {
    return "Not available";
  } else if (absValue < 1_000_000 && !skipLessThan) {
    return `<${prefix}$1M`;
  } else if (skipLessThan && absValue === 0) {
    return "$0";
  }

  if (absValue < 10_000_000 && absValue !== 0) {
    formattedValue = `${(Math.round(absValue / 100_000) / 10).toLocaleString(
      undefined,
      {
        minimumFractionDigits: 1,
      }
    )}M`;
  } else if (absValue < 100_000_000 && absValue !== 0) {
    formattedValue = `${Math.round(absValue / 1_000_000).toLocaleString()}M`;
  } else if (absValue < 1_000_000_000 && absValue !== 0) {
    formattedValue = `${Math.round(absValue / 10_000_00).toLocaleString()}M`;
  } else if (absValue < 10_000_000_000 && absValue !== 0) {
    formattedValue = `${(
      Math.round(absValue / 100_000_000) / 10
    ).toLocaleString(undefined, {
      minimumFractionDigits: 1,
    })}B`;
  } else {
    formattedValue = `${(
      Math.round(absValue / 100_000_000) / 10
    ).toLocaleString(undefined, {
      maximumFractionDigits: 0,
    })}B`;
  }

  return `${prefix}$${formattedValue}`;
};

// Format numbers in a better way than formatMoney for screen readers, e.g. read
// "$1.2B" as "1 billion 2 hundred million dollars" instead of "1 point 2 b dollars".
export const formatMoneySr = (rawValue: string | number) => {
  const value = typeof rawValue === "number" ? rawValue : parseInt(rawValue);
  const valueLength = Math.round(value).toString().length;
  const digits =
    valueLength > 12 ? 4 : valueLength === 9 || valueLength > 11 ? 3 : 2;

  return `$${new Intl.NumberFormat("en-US", {
    maximumSignificantDigits: digits,
  }).format(value)}`;
};

const isValidEconomyCode = (
  economyCode: string
): economyCode is keyof typeof economies => economyCode in economies;

const isValidRegionCode = (
  economyCode: string
): economyCode is keyof typeof regions => economyCode in regions;

export const getEconomyDisplayName = (economyCode?: string) => {
  const uppercaseEconomyCode = economyCode?.toUpperCase() || "";

  if (isValidEconomyCode(uppercaseEconomyCode)) {
    return economies[uppercaseEconomyCode];
  }

  if (isValidRegionCode(uppercaseEconomyCode)) {
    return regions[uppercaseEconomyCode];
  }

  return economyCode || "";
};

export const makePossessive = (text: string) => {
  const endsInS = text[text.length - 1] === "s";
  return `${text}${endsInS ? `’` : `’s`}`;
};

export const formatPercentage = (value: number) => {
  const percentage = value * 100;
  const roundedPercentage = Math.abs(Math.round(percentage));
  const prefix = percentage < 0 ? "-" : "";

  if (Math.abs(percentage) < 1) {
    return percentage < 0 ? ">-1%" : "<1%";
  }

  return `${prefix}${roundedPercentage.toLocaleString()}%`;
};

const isValidSectorCode = (
  sectorCode: string
): sectorCode is keyof typeof sectors => sectorCode in sectors;

export const getSectorDisplayName = (sectorCode?: string) => {
  const uppercaseSectorCode = sectorCode?.toUpperCase() || "";

  if (isValidSectorCode(uppercaseSectorCode)) {
    return sectors[uppercaseSectorCode];
  }

  return sectorCode || "";
};

const isValidSubSectorCode = (
  subSectorCode: string
): subSectorCode is keyof typeof subSectors => subSectorCode in subSectors;

export const getSubSectorDisplayName = (subSectorCode?: string) => {
  const uppercaseSubSectorCode = subSectorCode?.toUpperCase() || "";

  if (isValidSubSectorCode(uppercaseSubSectorCode)) {
    return subSectors[uppercaseSubSectorCode];
  }

  return subSectorCode || "";
};

export const formatDisplayList = (list: string[]) =>
  `${list.slice(0, -1).join(", ")}${
    list.length > 2 ? "," : ""
  } and ${list.slice(-1)}`;

export const isOtherSubSectorCode = (subSectorCode: string) =>
  getSubSectorDisplayName(subSectorCode).toLowerCase() === "other" ||
  getSubSectorDisplayName(subSectorCode).toLowerCase() === "others";

export const subSectorsSort = (a: string, b: string) => {
  const aDisplayName = getSubSectorDisplayName(a);
  const bDisplayName = getSubSectorDisplayName(b);

  if (isOtherSubSectorCode(a)) {
    return 1;
  }
  if (isOtherSubSectorCode(b)) {
    return -1;
  }

  if (aDisplayName.toLowerCase() === "total") {
    return -1;
  }
  if (bDisplayName.toLowerCase() === "total") {
    return 1;
  }

  return aDisplayName.localeCompare(bDisplayName);
};

type FormatFunction = (value: string, name: string, index: number) => unknown;
type SchemaItem = {
  name: string;
  format?: FormatFunction;
};

export function formatRows<T extends readonly SchemaItem[]>(
  rows: unknown,
  schema: T
) {
  if (!Array.isArray(rows)) {
    throw new Error("Data is not an array");
  }

  return rows
    .filter((row) =>
      schema.every((item, index) => {
        if (!row[item.name]) {
          console.error(
            `Skipping row ${index} because it is missing column "${
              item.name
            }": ${JSON.stringify(row)}`
          );
          return false;
        }

        return true;
      })
    )
    .map(
      (row, index) =>
        schema.reduce((rowWithKeys, item) => {
          const value = row[item.name];
          const formattedValue =
            item.format?.(value, item.name, index) ?? value;

          return {
            ...rowWithKeys,
            [item.name]: formattedValue,
          };
        }, {}) as {
          [K in T[number]["name"]]: Extract<
            T[number],
            { name: K }
          >["format"] extends infer F
            ? F extends FormatFunction
              ? ReturnType<F>
              : string
            : string;
        }
    );
}
